import React, { useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiCalendarRemove, mdiCashRefund } from "@mdi/js";
import BaseModal from "../BaseModal";
import CustomFormInput from "../forms/CustomFormInput";
import { classApi } from "../../api/classes";
import { organizationApi } from "../../api/organization";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { formatCurrencyFromMinor } from "../../utils/currency";
import "./CancelClassesModal.css";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getWeekDates() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() - dayOfWeek + i);
        dates.push({
            dayIndex: i,
            label: DAY_LABELS[i],
            dateStr: d.toISOString().split("T")[0],
            isToday: d.toDateString() === today.toDateString(),
        });
    }
    return dates;
}

const CancelClassesModal = ({ isOpen, onClose, onComplete, defaultDate }) => {
    const { user } = useAuth();
    const orgId = user?.orgId || "";
    // Whether the school offers refunds — gates the per-student refund step. When off, cancelling
    // simply removes the night's sessions with no refund UI (no make-up implication — unrelated).
    const [offersRefunds, setOffersRefunds] = useState(false);
    const [step, setStep] = useState(1);
    const [weekDates] = useState(getWeekDates);
    const [selectedDate, setSelectedDate] = useState(() => {
        if (defaultDate) return defaultDate;
        const today = new Date();
        return today.toISOString().split("T")[0];
    });
    const [classes, setClasses] = useState([]);
    const [selectedClassIds, setSelectedClassIds] = useState(new Set());
    const [reason, setReason] = useState("");
    const [isLoadingClasses, setIsLoadingClasses] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Step 2 state
    const [studentRefunds, setStudentRefunds] = useState([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);
    // 'makeup' = owe a make-up class, no money (default); 'refund' = refund students (then no make-up).
    const [refundMode, setRefundMode] = useState("makeup");

    // Load classes when date changes
    useEffect(() => {
        if (!isOpen || !selectedDate) return;
        loadClasses(selectedDate);
    }, [isOpen, selectedDate]);

    // Load the school's refund setting — gates whether the Student Refunds step appears.
    useEffect(() => {
        if (!isOpen || !orgId) return;
        organizationApi.getSettings(orgId)
            .then(res => setOffersRefunds(res?.offersRefunds === true))
            .catch(() => { /* non-fatal — defaults to no refunds */ });
    }, [isOpen, orgId]);

    // Reset on open/close
    useEffect(() => {
        if (isOpen) {
            if (defaultDate) setSelectedDate(defaultDate);
        } else {
            setStep(1);
            setClasses([]);
            setSelectedClassIds(new Set());
            setReason("");
            setStudentRefunds([]);
            setRefundMode("makeup");
        }
    }, [isOpen, defaultDate]);

    const loadClasses = async date => {
        setIsLoadingClasses(true);
        try {
            const res = await classApi.getClassesForDate(date);
            if (res.ok) {
                setClasses(res.data.classes || []);
            } else {
                toast.error(res.data?.message || "Failed to load classes");
            }
        } catch {
            toast.error("Failed to load classes");
        }
        setIsLoadingClasses(false);
    };

    const toggleClass = id => {
        setSelectedClassIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        const available = classes.filter(c => !c.isCancelled).map(c => c.id);
        setSelectedClassIds(new Set(available));
    };

    const handleClearAll = () => setSelectedClassIds(new Set());

    const handleNextToStep2 = async () => {
        if (selectedClassIds.size === 0) {
            toast.error("Select at least one class to cancel");
            return;
        }
        setIsLoadingStudents(true);
        setStep(2);

        // Load enrolments for each selected class
        const allStudents = [];
        for (const classId of selectedClassIds) {
            try {
                const res = await classApi.getEnrolments(classId);
                if (res.ok) {
                    const cls = classes.find(c => c.id === classId);
                    for (const e of res.data.enrolments || []) {
                        if (e.status === "confirmed" || e.status === "unconfirmed") {
                            allStudents.push({
                                enrolmentId: e.id,
                                classId,
                                className: cls?.name || "",
                                studentName: e.student
                                    ? `${e.student.firstName} ${e.student.lastName}`.trim()
                                    : "Unknown",
                                amountPaid: e.paymentAmount || 0,
                                paymentStatus: e.paymentStatus || "none",
                                refundAmount: "",
                            });
                        }
                    }
                }
            } catch {
                /* skip */
            }
        }
        setStudentRefunds(allStudents);
        setIsLoadingStudents(false);
    };

    const updateRefund = (idx, field, value) => {
        setStudentRefunds(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            // Only send refunds when the admin chose the "refund" path AND refunds are enabled.
            // Otherwise it's the make-up path — no money moves and every student is owed a make-up.
            const refunds = (offersRefunds && refundMode === "refund")
                ? studentRefunds
                    .filter(s => parseFloat(s.refundAmount) > 0)
                    .map(s => ({
                        enrolmentId: s.enrolmentId,
                        refundAmount: Math.round(parseFloat(s.refundAmount) * 100),
                    }))
                : [];

            const res = await classApi.cancelSessions(selectedDate, Array.from(selectedClassIds), reason, refunds);

            if (res.ok) {
                const data = res.data;
                const parts = [`${data.sessionsCancelled} class${data.sessionsCancelled !== 1 ? "es" : ""} cancelled`];
                if (data.refundsIssued > 0) {
                    parts.push(`${data.refundsIssued} refund${data.refundsIssued !== 1 ? "s" : ""} issued (${formatCurrencyFromMinor(data.totalRefunded)})`);
                }
                if (data.makeupsOwed > 0) {
                    parts.push(`${data.makeupsOwed} make-up${data.makeupsOwed !== 1 ? "s" : ""} owed`);
                }
                toast.success(parts.join(", "));
                const refundErrors = data.refundErrors || [];
                if (refundErrors.length > 0) {
                    toast.warning(
                        `${refundErrors.length} refund${refundErrors.length !== 1 ? "s" : ""} could not be processed. Those students are still owed a make-up. Check Stripe and refund them manually.`,
                        { autoClose: false }
                    );
                }
                onComplete?.();
                onClose();
            } else {
                toast.error(res.data?.message || "Failed to cancel sessions");
            }
        } catch {
            toast.error("Failed to cancel sessions");
        }
        setIsProcessing(false);
    };

    const availableClasses = classes.filter(c => !c.isCancelled);
    const cancelledClasses = classes.filter(c => c.isCancelled);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={step === 1 ? "Cancel Classes" : "Confirm Cancellation"}
            icon={step === 1 ? mdiCalendarRemove : mdiCashRefund}
            iconColor="var(--dl-color-danger)"
            maxWidth={640}
            footer={
                step === 1 ? (
                    <>
                        <button className="base-modal-cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn cancel-classes-danger-btn"
                            onClick={handleNextToStep2}
                            disabled={selectedClassIds.size === 0 || isLoadingClasses}>
                            Next
                        </button>
                    </>
                ) : (
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setStep(1)} disabled={isProcessing}>
                            Back
                        </button>
                        <button
                            className="base-modal-save-btn cancel-classes-danger-btn"
                            onClick={handleConfirm}
                            disabled={isProcessing}>
                            {isProcessing ? "Cancelling..." : "Confirm Cancellation"}
                        </button>
                    </>
                )
            }>
            {step === 1 && (
                <div className="cancel-classes-step1">
                    <p className="cancel-classes-hint">
                        Cancels classes for <strong>one night only</strong>. Students stay enrolled and a{" "}
                        <strong>make-up class will be added to their record</strong> for this missed session.{" "}
                        {offersRefunds
                            ? "You can also refund money for the missed session in the next step (optional)."
                            : "No money is refunded (this school has refunds turned off)."}
                    </p>

                    {/* Day picker */}
                    <div className="cancel-classes-day-picker">
                        {weekDates.map(d => (
                            <button
                                key={d.dateStr}
                                className={`cancel-classes-day-btn${selectedDate === d.dateStr ? " active" : ""}${d.isToday ? " today" : ""}`}
                                onClick={() => {
                                    setSelectedDate(d.dateStr);
                                    setSelectedClassIds(new Set());
                                }}>
                                <span className="cancel-classes-day-label">{d.label}</span>
                                <span className="cancel-classes-day-date">{d.dateStr.split("-")[2]}</span>
                            </button>
                        ))}
                    </div>

                    {/* Class list */}
                    {isLoadingClasses ? (
                        <p className="cancel-classes-loading">Loading classes...</p>
                    ) : classes.length === 0 ? (
                        <p className="cancel-classes-empty">No classes scheduled for this day.</p>
                    ) : (
                        <>
                            {availableClasses.length > 0 && (
                                <div className="cancel-classes-list-section">
                                    <div className="cancel-classes-list-header">
                                        <span>Classes ({availableClasses.length})</span>
                                        <div className="cancel-classes-quick-actions">
                                            <button
                                                type="button"
                                                onClick={handleSelectAll}
                                                className="btn-quick-action">
                                                Select All
                                            </button>
                                            <button type="button" onClick={handleClearAll} className="btn-quick-action">
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <ul className="cancel-classes-list">
                                        {availableClasses.map(c => (
                                            <li key={c.id} className="cancel-classes-item">
                                                <CustomFormInput
                                                    type="checkbox"
                                                    variant="purple"
                                                    layout="standard"
                                                    checked={selectedClassIds.has(c.id)}
                                                    onChange={() => toggleClass(c.id)}
                                                />
                                                <div className="cancel-classes-item-details">
                                                    <span className="cancel-classes-item-name">{c.name}</span>
                                                    <span className="cancel-classes-item-meta">
                                                        {c.startTime} to {c.endTime} | {c.enrolledCount} student
                                                        {c.enrolledCount !== 1 ? "s" : ""}
                                                        {c.termName ? ` | ${c.termName}` : ""}
                                                        {c.classType === "short-course" ? " (Short Course)" : ""}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {cancelledClasses.length > 0 && (
                                <div className="cancel-classes-list-section">
                                    <div className="cancel-classes-list-header">
                                        <span>Already cancelled ({cancelledClasses.length})</span>
                                    </div>
                                    <ul className="cancel-classes-list">
                                        {cancelledClasses.map(c => (
                                            <li
                                                key={c.id}
                                                className="cancel-classes-item cancel-classes-item--cancelled">
                                                <span
                                                    className="cancel-classes-cancelled-checkbox-placeholder"
                                                    aria-hidden="true"
                                                />
                                                <div className="cancel-classes-item-details">
                                                    <span className="cancel-classes-item-name">{c.name}</span>
                                                    <span className="cancel-classes-item-meta">
                                                        {c.startTime} to {c.endTime} | {c.enrolledCount} student
                                                        {c.enrolledCount !== 1 ? "s" : ""}
                                                        {c.termName ? ` | ${c.termName}` : ""}
                                                    </span>
                                                    <span className="cancel-classes-cancelled-tag">Cancelled</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}

                    {/* Reason */}
                    <div className="cancel-classes-reason">
                        <label className="cancel-classes-reason-label">Reason</label>
                        <input
                            type="text"
                            className="cancel-classes-reason-input"
                            placeholder="e.g. Instructor sick, Room unavailable"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="cancel-classes-step2">
                    {!offersRefunds ? (
                        <p className="cancel-classes-makeup-note">
                            These classes will be cancelled for this night. Students stay enrolled and a{" "}
                            <strong>make-up class will be added to their record</strong> for this missed session.
                            No money is refunded (refunds are off for this school).
                        </p>
                    ) : (
                        <div className="cancel-classes-decide">
                            <p className="cancel-classes-decide-q">How do you want to make it up to affected students?</p>
                            <div className="cancel-classes-decide-option">
                                <CustomFormInput
                                    type="radio"
                                    variant="purple"
                                    layout="standard"
                                    name="cancel-remedy"
                                    checked={refundMode === "makeup"}
                                    onChange={() => setRefundMode("makeup")}
                                    label={
                                        <>
                                            <strong>Log a make-up class</strong>: a make-up class is added to each
                                            student's record for this missed session; no money is refunded.
                                        </>
                                    }
                                />
                            </div>
                            <div className="cancel-classes-decide-option">
                                <CustomFormInput
                                    type="radio"
                                    variant="purple"
                                    layout="standard"
                                    name="cancel-remedy"
                                    checked={refundMode === "refund"}
                                    onChange={() => setRefundMode("refund")}
                                    label={
                                        <>
                                            <strong>Refund students</strong>: return money for the missed session;
                                            refunded students are not owed a make-up.
                                        </>
                                    }
                                />
                            </div>
                        </div>
                    )}

                    {offersRefunds && refundMode === "refund" && (
                      <>
                    {isLoadingStudents ? (
                        <p className="cancel-classes-loading">Loading affected students...</p>
                    ) : studentRefunds.length === 0 ? (
                        <p className="cancel-classes-empty">No enrolled students found for the selected classes.</p>
                    ) : (
                        <>
                            {studentRefunds.some(s => s.amountPaid > 0) ? (
                                <p className="cancel-classes-refund-quickset-label">
                                    Enter how much to refund each student for this missed session, for example the
                                    value of one night. Leave a box blank to refund nothing.
                                </p>
                            ) : (
                                <p className="cancel-classes-no-payments">No students have a recorded payment, so there are no refunds to issue.</p>
                            )}

                            <div className="cancel-classes-student-list">
                                {studentRefunds.map((s, idx) => {
                                    if (!(s.amountPaid > 0)) return null;
                                    return (
                                    <div key={s.enrolmentId} className="cancel-classes-student-row">
                                        <div className="cancel-classes-student-info">
                                            <span className="cancel-classes-student-name">{s.studentName}</span>
                                            <span className="cancel-classes-student-class">{s.className}</span>
                                        </div>
                                        <div className="cancel-classes-student-refund">
                                            <span className="cancel-classes-refund-label">Refund $</span>
                                            <input
                                                type="number"
                                                className="cancel-classes-refund-input"
                                                value={s.refundAmount}
                                                onChange={e => updateRefund(idx, "refundAmount", e.target.value)}
                                                min="0"
                                                step="1"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                      </>
                    )}
                </div>
            )}
        </BaseModal>
    );
};

export default CancelClassesModal;
